import React from 'react'
import PropTypes from 'prop-types'
import { Button, PercentMeter } from 'components/UIElements'
import SurveyQuestion from './SurveyQuestion'
import {
  QUESTION_TYPES,
  INSTRUMENT_EVENTS_MAP,
  MEDIA_ELEMENTS,
} from 'utils/constants'
import returnBrowserType from 'utils/browser'
import { closeIcon } from '../../../assets/assets'
import {
  formatSubmissionSurvey,
  checkLogicAndComplete,
  mergeStoredSubmissionWithBase,
  findSiblingsOrder,
  getNextRenderableQuestionId,
  getReviewOrFinishDestination,
} from '../utils/instrumentUtils'
import {
  Introduction,
  SelectOne,
  SelectMultiple,
  TextEntry,
  TextPrompt,
  Integer,
  Likert,
  Decimal,
  ImageCapture,
  TimeType,
  DateType,
  DateTimeType,
  NumericRatingScale,
  MultipleField,
  Matrix,
  VAScale,
  VAScale2021,
  VASHorizontal,
  ScribbleType,
  Sibling,
  AudioType,
  Timer,
  TimedMatchingGrid,
  Review,
  ResumeReview,
  ACQ7View,
  HorizontalSelectOne,
} from './QuestionTypes/QuestionTypes'
import '../stylesheets/WebSurveys.scss'
import { formatAndSanitizeHTML } from 'utils/misc'
import { getInstrumentFromLocalStorage } from '../../Activities/utils/instrumentUtils'
import {
  computeParticipantScores,
  computeInstrumentScoreTotal,
} from '../utils/scoringUtils'
import { NextButton } from './QuestionFooter'
import { navigateTo } from 'navigation'
import { logSentryEvent, SENTRY_MESSAGES } from '../../../utils/sentry'

const getSurveySubmissionQuestion = (strings) => {
  return {
    type: 'submission',
    label: strings.survey_confirm_submit,
  }
}

const getSurveyReviewQuestion = () => {
  return {
    type: 'review',
    label: 'Review',
  }
}

class WebSurveyPage extends React.Component {
  constructor(props) {
    super(props)
    this.questionRefs = {}
    this.timeoutIds = []
    this._isMounted = false
    this.surveyContainerRef = React.createRef()
    this._lastActiveQuestionId = null
    this._savedScrollTop = 0
    this._ignoreLayoutScroll = false
    this._lastViewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0
    this.state = {
      currentProgress: 0,
      completionDivisor: 1,
      browser: 'chrome',
      scrollTop: 0,
      scrollingDown: false,
      scrollingUp: false,
      // GOLD-12178: Next/Back scroll between mounted questions. TMG uses
      // this id so its fixed timer only shows on the TMG screen.
      activeQuestionId: props.currentSurvey?.[0] || null,
    }
  }

  pauseAllMedia = () => {
    try {
      MEDIA_ELEMENTS.forEach((tag) => {
        const elements = document.querySelectorAll(tag)
        elements.forEach((el, index) => {
          try {
            if (!el.paused) {
              el.pause()
              el.currentTime = 0
            }
          } catch (err) {
            console.error(
              `Error stopping ${tag} element at index ${index}:`,
              err,
            )
          }
        })
      })
    } catch (err) {
      console.error('Failed to stop media elements:', err)
    }
  }

  componentDidMount() {
    this._isMounted = true
    const {
      pendingInstruments,
      instruments,
      webSurvey,
      setSubmissionSurvey,
      setAllRequiredAnswered,
      isArticle,
      onSurveySubmit,
      submissionSurvey,
      participantId,
    } = this.props

    const { browser } = this.state

    if (isArticle) {
      window.open(`${webSurvey.url}`)
      onSurveySubmit(submissionSurvey, isArticle, webSurvey)
      navigateTo('/my-activities')
    }
    const { id } = webSurvey

    let formattedSurvey
    const idArr = pendingInstruments.map((instrument) => instrument.identity)
    const identityIndex = idArr.indexOf(id)
    const instrument = pendingInstruments[identityIndex]
    const preferredInstanceId =
      submissionSurvey?.instance_id || webSurvey?.instance_id
    const v2Instrument =
      instruments?.find((item) => {
        if (!preferredInstanceId) return item?.identity === id
        const itemInstanceId =
          item?.instance_id || item?.instances?.[0]?.instance_id
        return (
          item?.identity === id &&
          itemInstanceId &&
          String(itemInstanceId) === String(preferredInstanceId)
        )
      }) || instruments?.find((item) => item?.identity === id)

    const { currentSurvey } = this.props
    const hasExistingProgress =
      (currentSurvey || []).length > 0 ||
      (submissionSurvey?.questions || []).some(
        (q) => Array.isArray(q?.answer) && q.answer.length > 0,
      )

    // Opening "Review & Edit" from calendar passes preloaded questions; do not replace with template.
    if (!submissionSurvey?.__reviewCompletedEntry && !hasExistingProgress) {
      if (instrument) {
        formattedSurvey = formatSubmissionSurvey(instrument)
      }
      if (v2Instrument) {
        formattedSurvey = formatSubmissionSurvey({
          ...v2Instrument,
          instance_id: preferredInstanceId || webSurvey.instance_id,
        })
      }
      // Merge any stored progress (Save & Resume) so we don't overwrite with empty template
      if (formattedSurvey && participantId) {
        const storedProgress = getInstrumentFromLocalStorage({
          participantId,
          instrumentId: id || webSurvey.identity,
          instanceId:
            formattedSurvey.instance_id ||
            preferredInstanceId ||
            webSurvey.instance_id,
        })
        const order0 = webSurvey?.order?.[0] || []
        const merged = mergeStoredSubmissionWithBase(
          formattedSurvey,
          storedProgress,
          order0,
        )
        setSubmissionSurvey(merged)
      } else if (formattedSurvey) {
        setSubmissionSurvey(formattedSurvey)
      }
    }

    const restoredQuestions =
      this.restoreCurrentSurveyIfNeeded(setAllRequiredAnswered)
    const activeSurvey = restoredQuestions || this.props.currentSurvey || []
    if (activeSurvey.length > 0) {
      const activeQuestionId = activeSurvey[activeSurvey.length - 1]
      this._lastActiveQuestionId = activeQuestionId
      this.updateProgress(activeQuestionId)
      const restoreScrollTimeoutId = setTimeout(() => {
        this.scrollToActiveQuestion()
      }, 0)
      this.timeoutIds.push(restoreScrollTimeoutId)
    } else if (this._lastActiveQuestionId) {
      this.updateProgress(this._lastActiveQuestionId)
      const restoreScrollTimeoutId = setTimeout(() => {
        this.scrollToActiveQuestion()
      }, 0)
      this.timeoutIds.push(restoreScrollTimeoutId)
    } else if (webSurvey?.order?.[0]?.[0]) {
      // Fresh resume landing on resume-review / first question via renderIntroduction
      this.updateProgress(webSurvey.order[0][0])
    }

    window.addEventListener('resize', this.handleViewportResize)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    const browserType = returnBrowserType()
    if (browserType !== browser) {
      this.setState({ browser: browserType })
    }
  }

  restoreCurrentSurveyIfNeeded = (setAllRequiredAnswered) => {
    const { currentSurvey, submissionSurvey, webSurvey, participantId } =
      this.props
    if ((currentSurvey || []).length > 0 || !setAllRequiredAnswered) return null

    const order0 = webSurvey?.order?.[0] || []
    if (!order0.length) return null

    const idToIndex = new Map(order0.map((qid, idx) => [qid, idx]))
    const storedInstrument = participantId
      ? getInstrumentFromLocalStorage({
        participantId,
        instrumentId: webSurvey.id || webSurvey.identity,
        instanceId:
          submissionSurvey?.instance_id || webSurvey?.instance_id,
      })
      : null

    const merged = [
      ...(submissionSurvey?.questions || []),
      ...((storedInstrument && storedInstrument.questions) || []),
    ]

    let maxI = -1
    merged.forEach((q) => {
      if (!q?.id) return
      const t = q.type
      if (
        t === QUESTION_TYPES.prompt ||
        t === 'submission' ||
        t === 'review' ||
        t === 'resume-review'
      ) {
        return
      }
      if (t === QUESTION_TYPES.introduction) {
        const i = idToIndex.get(q.id)
        if (i !== undefined) maxI = Math.max(maxI, i)
        return
      }
      if (!Array.isArray(q.answer) || q.answer.length === 0) return
      const i = idToIndex.get(q.id)
      if (i !== undefined) maxI = Math.max(maxI, i)
    })

    if (maxI < 0) return null

    let questionsToLoad = order0.slice(0, maxI + 1)
    const introId = order0[0]
    if (webSurvey?.questions?.[introId]?.type === QUESTION_TYPES.introduction) {
      questionsToLoad = questionsToLoad.filter((qid) => qid !== introId)
    }
    if (!questionsToLoad.length) return null

    setAllRequiredAnswered(true, null, questionsToLoad)
    this._lastActiveQuestionId = questionsToLoad[questionsToLoad.length - 1]
    return questionsToLoad
  }

  getActiveQuestionId = () => {
    // Prefer the question the user is actually on (Back / edit / Next),
    // not always the furthest id in currentSurvey.
    if (this._lastActiveQuestionId) return this._lastActiveQuestionId
    const { currentSurvey } = this.props
    if (currentSurvey?.length > 0) {
      return currentSurvey[currentSurvey.length - 1]
    }
    return null
  }

  getQuestionElement = (questionId) => {
    if (!questionId) return null
    return (
      document.getElementById(questionId) ||
      this.questionRefs[questionId] ||
      null
    )
  }

  isQuestionInView = (el) => {
    if (!el?.getBoundingClientRect) return false
    const rect = el.getBoundingClientRect()
    const container = this.surveyContainerRef?.current
    const topBound = container?.getBoundingClientRect?.().top ?? 0
    return (
      rect.top >= topBound - 16 &&
      rect.top <= topBound + window.innerHeight * 0.55
    )
  }

  suppressLayoutScroll = (ms = 1000) => {
    this._ignoreLayoutScroll = true
    if (this._layoutScrollTimeout) {
      clearTimeout(this._layoutScrollTimeout)
    }
    this._layoutScrollTimeout = setTimeout(() => {
      this._ignoreLayoutScroll = false
      this._layoutScrollTimeout = null
    }, ms)
    this.timeoutIds.push(this._layoutScrollTimeout)
  }

  scrollToActiveQuestion = (retries = 15) => {
    if (!this._isMounted || this._ignoreLayoutScroll) return
    const activeQuestionId = this.getActiveQuestionId()
    if (!activeQuestionId) {
      const container = this.surveyContainerRef?.current
      if (container && this._savedScrollTop > 0) {
        container.scrollTop = this._savedScrollTop
      }
      return
    }

    const el = this.getQuestionElement(activeQuestionId)
    if (el?.scrollIntoView) {
      // GOLD-12345: Next already scrolled this question into view. A follow-up
      // resize (mobile URL bar) must not yank back to the start of the page.
      if (this.isQuestionInView(el)) return
      el.scrollIntoView({ block: 'start' })
    } else if (retries > 0) {
      const retryTimeoutId = setTimeout(
        () => this.scrollToActiveQuestion(retries - 1),
        100,
      )
      this.timeoutIds.push(retryTimeoutId)
    }
  }

  handleSurveyLayoutChange = () => {
    if (!this._isMounted || this._ignoreLayoutScroll) return
    // Wait two frames so resize/visibility layout has settled.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.scrollToActiveQuestion())
    })
  }

  handleViewportResize = () => {
    const nextHeight = window.innerHeight
    const heightDelta = Math.abs(
      nextHeight - (this._lastViewportHeight || nextHeight),
    )
    this._lastViewportHeight = nextHeight
    // Mobile browser chrome show/hide fires resize without a real layout reset.
    if (heightDelta > 0 && heightDelta < 180) return
    this.handleSurveyLayoutChange()
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      const { setAllRequiredAnswered } = this.props
      const restoredQuestions =
        this.restoreCurrentSurveyIfNeeded(setAllRequiredAnswered)
      if (restoredQuestions?.length) {
        this._lastActiveQuestionId =
          restoredQuestions[restoredQuestions.length - 1]
      }
      const activeQuestionId = this.getActiveQuestionId()
      if (activeQuestionId) {
        this.updateProgress(activeQuestionId)
      }
      this.timeoutIds.push(
        setTimeout(() => this.handleSurveyLayoutChange(), 100),
      )
    }
  }

  onSurveyContainerScroll = () => {
    const container = this.surveyContainerRef?.current
    if (container) {
      this._savedScrollTop = container.scrollTop
    }
  }

  componentDidUpdate(prevProps) {
    const { currentSurvey, webSurvey } = this.props
    if (prevProps.currentSurvey !== currentSurvey && currentSurvey?.length > 0) {
      const activeQuestionId = currentSurvey[currentSurvey.length - 1]
      this._lastActiveQuestionId = activeQuestionId
      // Sync meter on resume/restore (empty → populated) or when the path is replaced
      // (e.g. edit from resume-review). Skip incremental Next adds so sibling pages
      // keep progress keyed to the first question in the group.
      const prevLen = prevProps.currentSurvey?.length || 0
      const nextLen = currentSurvey.length
      if (prevLen === 0 || nextLen < prevLen || nextLen - prevLen > 1) {
        this.updateProgress(activeQuestionId)
      }
    }

    // When survey order arrives/changes (e.g. resume-review injected), refresh meter.
    const prevOrderLen = prevProps.webSurvey?.order?.[0]?.length || 0
    const nextOrderLen = webSurvey?.order?.[0]?.length || 0
    if (nextOrderLen > 0 && nextOrderLen !== prevOrderLen) {
      const activeQuestionId =
        this.getActiveQuestionId() || webSurvey.order[0][0]
      this.updateProgress(activeQuestionId)
    }

    // GOLD-12178: Seed the visible question once currentSurvey is ready
    // (intro / first item) so TMG is not treated as off-screen on first paint.
    if (!this.state.activeQuestionId && this.props.currentSurvey?.[0]) {
      this.setState({ activeQuestionId: this.props.currentSurvey[0] })
    }

    // Autosave whenever submissionSurvey questions change
    const {
      submissionSurvey,
      onSaveInstrumentToLocalStorage,
      isPauseResumeSupported,
    } = this.props
    const prevQuestions = prevProps.submissionSurvey?.questions || []
    const currentQuestions = submissionSurvey?.questions || []

    // Check if questions array has changed (new answer added or updated)
    if (
      currentQuestions.length > 0 &&
      JSON.stringify(prevQuestions) !== JSON.stringify(currentQuestions)
    ) {
      // Debounce to avoid excessive saves, but save often enough so we don't lose questions
      if (this.autosaveTimeout) {
        clearTimeout(this.autosaveTimeout)
      }

      this.autosaveTimeout = setTimeout(() => {
        if (
          this._isMounted &&
          onSaveInstrumentToLocalStorage &&
          isPauseResumeSupported
        ) {
          onSaveInstrumentToLocalStorage()
        }
      }, 400) // Shorter delay so closing soon after answering still has time to save
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    window.removeEventListener('resize', this.handleViewportResize)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    // Clear all pending timeouts to prevent state updates on unmounted component
    this.timeoutIds.forEach((timeoutId) => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    })
    this.timeoutIds = []

    // Clear pending autosave timer on unmount.
    // Do not dispatch a save here because route transitions can reset survey state
    // and race with this save, which may persist stale/empty data.
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
      this.autosaveTimeout = null
    }

    this.props.closeModal?.()
    this.pauseAllMedia()
  }

  renderQuestions = (survey) => {
    const { currentSurvey } = this.props
    const { questions, siblings = {} } = survey
    const copyrightText = survey?.metadata?.copyright_text
    // sanitize and remove any editor attributions (e.g., Froala)
    const cleanedCopyright = formatAndSanitizeHTML(
      (copyrightText || '').replace(
        /<p[^>]*data-f-id=["']pbf["'][\s\S]*?<\/p>/gi,
        '',
      ),
    )
    return (
      <div>
        {currentSurvey.map((questionId) => {
          const questionEl = this.renderQuestion(
            questions[questionId],
            questionId,
            siblings,
          )
          return (
            <div
              key={`q-wrap-${questionId}`}
              ref={(el) => {
                if (el) this.questionRefs[questionId] = el
              }}>
              {questionEl}
            </div>
          )
        })}
        {cleanedCopyright ? (
          <div className='websurvey-copyright'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(cleanedCopyright),
              }}
            />
          </div>
        ) : null}
      </div>
    )
  }

  renderBackButton = (id, disabled = false) => {
    const { strings } = this.props
    return (
      <Button
        primary
        grey
        icon='fas fa-angle-up'
        content={strings.back}
        onClick={(e) => {
          e.stopPropagation()
          this.scrollToPreviousQuestion(id)
        }}
        disabled={disabled}
      />
    )
  }

  renderQuestion = (question, id, siblings) => {
    const {
      deleteStagedFile,
      closeModal,
      completeQuestion,
      currentSurvey,
      firstName,
      isCaregiver,
      onSurveySubmit,
      openModal,
      params,
      participantId,
      selectedLocale,
      setAllRequiredAnswered,
      strings,
      submissionLoading,
      submissionSurvey,
      webSurvey,
      uploadV2File,
      studyId,
      humiIsabellaDisabled,
    } = this.props
    const reviewFunctionalitySupported =
      webSurvey?.metadata?.review_screen?.enabled ?? false
    const { scrollTop, scrollingDown, scrollingUp } = this.state
    const surveyScroll = { scrollTop, scrollingDown, scrollingUp }
    const isReviewScreen = webSurvey?.metadata?.review_screen?.enabled ?? false
    const isIntroOnly = Object.keys(webSurvey?.questions)?.length === 1
    const _question = reviewFunctionalitySupported
      ? question ||
      (isIntroOnly || !isReviewScreen
        ? getSurveySubmissionQuestion(strings)
        : getSurveyReviewQuestion(strings)) ||
      getSurveySubmissionQuestion(strings)
      : question || getSurveySubmissionQuestion(strings)

    const { type, sibling_id: siblingId } = _question

    const storedInstrument = getInstrumentFromLocalStorage({
      instrumentId: submissionSurvey.identity || webSurvey.id,
      participantId,
      instanceId:
        submissionSurvey?.instance_id || webSurvey?.instance_id,
    })

    const storedAnswer = storedInstrument?.questions?.find(
      (item) => item.id === id,
    )?.answer

    const order0 = webSurvey?.order?.[0] || []
    const isLastQuestion = order0.length > 0 && order0[order0.length - 1] === id
    const isConsent = webSurvey?.type === 'CONSENT'

    // When resuming, wrap setAllRequiredAnswered to prevent truncating currentSurvey
    // This prevents questions from removing other questions when they initialize
    const wrappedSetAllRequiredAnswered = (...args) => {
      const [allAnswered, questionId, orderArg, siblingOrder = []] = args
      const currentSurveyArr = Array.isArray(currentSurvey) ? currentSurvey : []
      const siblingOrderArr = Array.isArray(siblingOrder) ? siblingOrder : []
      // Always allow loading all questions (e.g. resume/edit)
      if (allAnswered === true) {
        setAllRequiredAnswered(...args)
        return
      }
      // When truncating (allAnswered === false): only allow if this question is
      // the last in currentSurvey. If user has gone back, do not truncate so we
      // don't remove questions ahead (GOLD-11360).
      if (allAnswered === false && questionId) {
        const questionIndex = currentSurveyArr.indexOf(questionId)
        const hasSiblingInSurvey = siblingOrderArr.some(
          (sid) => currentSurveyArr.indexOf(sid) !== -1,
        )
        if (questionIndex === -1 && !hasSiblingInSurvey) {
          return
        }
        if (currentSurveyArr.length > 0) {
          const isLastQuestion = siblingOrderArr.length
            ? Math.max(
              ...siblingOrderArr.map((sid) => currentSurveyArr.indexOf(sid)),
            ) ===
            currentSurveyArr.length - 1
            : currentSurveyArr[currentSurveyArr.length - 1] === questionId
          if (!isLastQuestion) return
        }
      }
      // Only allow if no stored instrument (normal flow) or we already handled true above
      if (!storedInstrument || allAnswered === true) {
        setAllRequiredAnswered(...args)
      }
    }

    // Special synthetic score-summary page injected at the end of order[0]
    // for instruments with scoring + horizontal_select_one and no review screen.
    if (type === 'score-summary' || id === 'score-summary') {
      const scoresArray = Array.isArray(webSurvey?.scores) ? webSurvey.scores : []
      const explicitlyFlagged = scoresArray.filter(
        (score) => score?.display_score_to_participant === true,
      )
      const firstDisplayedScore =
        explicitlyFlagged.length > 0
          ? explicitlyFlagged[0]
          : (webSurvey?.metadata?.display_score_to_participant === true &&
            scoresArray.length > 0)
            ? scoresArray[0]
            : scoresArray[0]

      const scoreName =
        firstDisplayedScore?.name ||
        strings?.score_total ||
        'Score'
      const participantScores = this.getInstrumentParticipantScores()
      const fallbackTotal = this.getInstrumentScoreTotal()
      const items = participantScores.length
        ? participantScores
        : [{ id: 'total', name: scoreName, value: fallbackTotal }]

      return (
        <SurveyQuestion id={id}>
          <div className='survey-question score-summary'>
            <div className='score-summary-page'>
              {items.map((score) => (
                <div
                  className='score-summary-item'
                  key={score?.id || score?.name}
                >
                  <div className='score-summary-name'>{score?.name}</div>
                  <div className='score-summary-value'>
                    {score?.value ?? 0}
                  </div>
                </div>
              ))}
            </div>
            <div className='button-list'>
              {this.renderBackButton(id)}
              <NextButton
                disabled={false}
                content={strings?.next || 'Next'}
                completionFunc={() => this.renderNextQuestion(id, true)}
              />
            </div>
          </div>
        </SurveyQuestion>
      )
    }

    const questionIndexInOrder = order0.indexOf(id)
    const hasRestoredPastThisQuestion = (currentSurvey || []).some(
      (qid) => order0.indexOf(qid) > questionIndexInOrder,
    )

    const props = {
      ref: (el) => {
        if (el) this.questionRefs[id] = el
      },
      deleteStagedFile,
      closeModal,
      checkLogicAndComplete,
      completeQuestion,
      currentSurvey,
      firstName,
      id,
      isCaregiver,
      key: id,
      nextQ: this.renderNextQuestion,
      nextQId: this.nextQuestionId,
      params,
      participantId,
      openModal,
      question: reviewFunctionalitySupported
        ? question ||
        (id === 'submit-id' || isIntroOnly
          ? getSurveySubmissionQuestion(strings)
          : getSurveyReviewQuestion(strings))
        : question || getSurveySubmissionQuestion(strings),
      renderBackButton: this.renderBackButton,
      selectedLocale,
      setAllRequiredAnswered: wrappedSetAllRequiredAnswered,
      strings,
      submissionLoading,
      submissionSurvey,
      surveyScroll,
      webSurvey,
      uploadV2File,
      studyId,
      humiIsabellaDisabled,
      scrollToPreviousQuestion: this.scrollToPreviousQuestion,
      isLastQuestion,
      isConsent,
      isSibling: false,
      siblingOrder: [],
      storedInstrument,
      storedAnswer,
      // Only auto-skip intro when resume has not already restored questions past it
      skipIntro:
        !!storedInstrument &&
        type === QUESTION_TYPES.introduction &&
        !hasRestoredPastThisQuestion,
      reviewFunctionalitySupported,
      // Suppress auto-advance when resuming to prevent unwanted navigation
      suppressAutoAdvance: !!storedInstrument,
      // GOLD-12178: True only for the question currently scrolled into view.
      isActiveQuestion: this.state.activeQuestionId === id,
    }

    if (id === 'submit-id' && reviewFunctionalitySupported) {
      return (
        <Introduction
          {...props}
          onSurveySubmit={onSurveySubmit}
          submission
          submissionSurvey={submissionSurvey}
          ref={(el) => {
            this.questionRefs[id] = el
          }}
        />
      )
    }

    if (siblingId) {
      const siblingsOrder = findSiblingsOrder(
        webSurvey,
        submissionSurvey,
        siblingId,
        id,
      )

      const isFirstSibling = siblingsOrder?.length && siblingsOrder[0] === id
      return (
        isFirstSibling && (
          <Sibling
            {...props}
            allQuestions={webSurvey.questions}
            order={siblingsOrder}
            siblingId={siblingId}
          />
        )
      )
    }

    switch (type) {
      case QUESTION_TYPES.introduction:
        return <Introduction {...props} />
      case QUESTION_TYPES.selectOne:
        return (
          <SelectOne
            ref={(el) => {
              this.questionRefs[id] = el
            }}
            {...props}
          />
        )
      case QUESTION_TYPES.horizontalSelectOne:
        return (
          <HorizontalSelectOne
            ref={(el) => {
              this.questionRefs[id] = el
            }}
            {...props}
          />
        )
      case QUESTION_TYPES.selectMultiple:
        return (
          <SelectMultiple
            ref={(el) => {
              this.questionRefs[id] = el
            }}
            {...props}
          />
        )
      case QUESTION_TYPES.text:
        return (
          <TextEntry
            ref={(el) => {
              this.questionRefs[id] = el
            }}
            {...props}
          />
        )
      case QUESTION_TYPES.prompt:
        return (
          <TextPrompt
            ref={(el) => {
              this.questionRefs[id] = el
            }}
            {...props}
          />
        )
      case QUESTION_TYPES.decimal:
        return (
          <Decimal
            ref={(el) => {
              this.questionRefs[id] = el
            }}
            {...props}
          />
        )
      case QUESTION_TYPES.imageCapture:
        return (
          <ImageCapture
            ref={(el) => {
              this.questionRefs[id] = el
            }}
            renderDeleteConfirmationModal={this.renderDeleteConfirmationModal}
            renderCameraHelpModal={this.renderCameraHelpModal}
            {...props}
          />
        )
      case QUESTION_TYPES.integer:
        return (
          <Integer
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.likert:
        return (
          <Likert
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.time:
        return (
          <TimeType
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.date:
        return (
          <DateType
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.datetime:
        return (
          <DateTimeType
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.numericRatingScale:
        return (
          <NumericRatingScale
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.multipleField:
        return (
          <MultipleField
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.matrix:
        return (
          <Matrix
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.vaScale:
        return (
          <VAScale
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.vaScale2021:
        return (
          <VAScale2021
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.vasHorizontal:
        return (
          <VASHorizontal
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.submission:
        return (
          <Introduction
            {...props}
            onSurveySubmit={onSurveySubmit}
            submission
            submissionSurvey={submissionSurvey}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.scribble:
        return (
          <ScribbleType
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.audio:
        return (
          <AudioType
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.timer:
        return (
          <Timer
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.timedMatchingGrid:
        // GOLD-12178: SDMT / Timed Matching Grid is a full-page question
        // (not siblings). Render the dedicated component so the timer and
        // grid are not dropped as an unknown type.
        return (
          <TimedMatchingGrid
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.review:
        return (
          <Review
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.resumeReview:
        return (
          <ResumeReview
            {...props}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      case QUESTION_TYPES.acq7:
        const { ref, ...acqProps } = props
        return (
          <ACQ7View
            {...acqProps}
            ref={(el) => {
              this.questionRefs[id] = el
            }}
          />
        )
      default:
        return null
    }
  }

  renderIntroduction = (survey) => {
    const { order, questions } = survey
    const [id, nextId] = order?.[0] ?? []
    const question = questions?.[id]
    if (!question) {
      return null
    }
    return this.renderQuestion(question, id, nextId)
  }

  nextQuestionId = (id) => {
    const { webSurvey } = this.props
    const reviewFunctionalitySupported =
      webSurvey?.metadata?.review_screen?.enabled ?? false
    const { order } = webSurvey
    const scoreSummaryId = 'score-summary'
    const scoreSummaryIndex = order?.[0]?.indexOf(scoreSummaryId) ?? -1

    // score-summary is the final scoring screen; never show review after it.
    if (id === scoreSummaryId) {
      return 'submit-id'
    }

    // After confirming review, jump directly to score-summary (when present).
    if (id === 'review' && reviewFunctionalitySupported) {
      return scoreSummaryIndex !== -1 ? scoreSummaryId : 'submit-id'
    }

    const currentIndex = order[0].indexOf(id)
    const isLastQuestion = currentIndex === order[0].length - 1
    // GOLD-12260: Shared helper so Next after the last question and a
    // zip that still lists 'review' as the next id both skip an empty
    // Review when only TMG (in_review off) remains.
    const reviewDestination = getReviewOrFinishDestination({
      reviewEnabled: reviewFunctionalitySupported,
      questions: webSurvey?.questions,
      hasScoreSummary: scoreSummaryIndex !== -1,
    })

    // If score-summary is injected at the end of order[0], show the review
    // screen after the last "real" question (right before score-summary).
    const shouldShowReviewAfterLastRealQuestion =
      reviewFunctionalitySupported &&
      scoreSummaryIndex !== -1 &&
      scoreSummaryIndex > 0 &&
      order[0][scoreSummaryIndex - 1] === id
    if (shouldShowReviewAfterLastRealQuestion) {
      return reviewDestination
    }

    if (isLastQuestion && reviewFunctionalitySupported) {
      return reviewDestination
    }

    if (isLastQuestion && !reviewFunctionalitySupported) {
      return 'submit-id'
    }

    const nextId = order[0][currentIndex + 1]
    // GOLD-12260: Authoring may still put 'review' after TMG. Only skip
    // that screen when nothing else is reviewable — do not treat TMG
    // itself as a reason to jump to submit.
    if (nextId === 'review' && reviewDestination !== 'review') {
      return reviewDestination
    }
    return nextId
  }

  nextSiblingQuestionIds = (id) => {
    const { submissionSurvey, webSurvey } = this.props
    const { order, questions } = webSurvey
    const order0 = order?.[0] || []

    const question = questions?.[id]
    const currentIndex = order0.indexOf(id)
    const isLastInOrder = currentIndex === order0.length - 1

    // Last question in linear order with no sibling group: nothing to batch here
    // (non-sibling last questions are added via the synchronous addQuestion path).
    if (!question?.sibling_id) {
      if (isLastInOrder) return []
      return [id]
    }

    // Last-in-order can still be the only renderable row in a sibling page (e.g. after
    // skipping unsupported types like integrated_device); must return sibling ids.
    const siblingId = question.sibling_id
    const siblingIds = findSiblingsOrder(
      webSurvey,
      submissionSurvey,
      siblingId,
      id,
    )

    return siblingIds?.length ? siblingIds : [id]
  }

  updateProgress = (questionId) => {
    if (!this._isMounted || !questionId) return
    const { webSurvey } = this.props
    const order = webSurvey?.order?.[0] || []
    const completionDivisor = Math.max(order.length, 1)
    const progress = order.indexOf(questionId)
    // GOLD-12178: Keep the visible question id so TMG can hide/restart
    // its timer when the participant scrolls to another question.
    this.setState({
      completionDivisor,
      currentProgress: progress === -1 ? completionDivisor : progress,
      activeQuestionId: questionId || this.state.activeQuestionId,
    })
  }

  scrollToPreviousQuestion = (id, index = '1') => {
    this.pauseAllMedia()
    this.suppressLayoutScroll()
    const {
      currentSurvey,
      webSurvey,
      setAllRequiredAnswered,
      submissionSurvey,
      participantId,
    } = this.props

    const { order, questions, siblings } = webSurvey
    const firstQuestionID = order[0][0]
    let prevQuestionID
    let needsRerender = false

    // Check if there's a stored instrument (resuming scenario)
    const storedInstrument = getInstrumentFromLocalStorage({
      instrumentId: submissionSurvey.identity || webSurvey.id,
      participantId,
      instanceId:
        submissionSurvey?.instance_id || webSurvey?.instance_id,
    })

    // ✅ Edit mode (Review → pencil click)
    if (index === '0') {
      prevQuestionID = id
      const isInSurvey = currentSurvey.includes(prevQuestionID)
      const targetIndex = order[0].indexOf(prevQuestionID)

      if (!isInSurvey && targetIndex !== -1) {
        // GOLD-11478: load from first question through furthest answered in survey order
        // (old logic only loaded "answered ids" → dropped earlier questions from Review/submit)
        const order0 = order[0]
        const idToIndex = new Map(order0.map((qid, idx) => [qid, idx]))
        const merged = [
          ...(submissionSurvey?.questions || []),
          ...((storedInstrument && storedInstrument.questions) || []),
        ]
        let maxI = -1
        merged.forEach((q) => {
          if (!q?.id) return
          const t = q.type
          if (
            t === QUESTION_TYPES.introduction ||
            t === QUESTION_TYPES.prompt ||
            t === 'submission' ||
            t === 'review' ||
            t === 'resume-review'
          ) {
            return
          }
          if (!Array.isArray(q.answer) || q.answer.length === 0) return
          const i = idToIndex.get(q.id)
          if (i !== undefined) maxI = Math.max(maxI, i)
        })
        const targetIdx = idToIndex.get(prevQuestionID)
        if (targetIdx !== undefined) {
          maxI = Math.max(maxI, targetIdx)
        }
        const questionsToLoad = maxI >= 0 ? order0.slice(0, maxI + 1) : order0

        setAllRequiredAnswered(true, null, questionsToLoad)
        needsRerender = true
      }
    } else {
      // Normal back navigation
      const currentIndex = currentSurvey.indexOf(id)
      prevQuestionID =
        currentIndex <= 0
          ? firstQuestionID
          : currentSurvey[currentIndex - Number(index)]
    }

    const prevQuestion = questions?.[prevQuestionID]
    const prevQuestionSiblingId = prevQuestion?.sibling_id
    if (prevQuestionSiblingId && siblings?.[prevQuestionSiblingId]?.length) {
      prevQuestionID = siblings[prevQuestionSiblingId][0]
    }

    this.updateProgress(prevQuestionID)
    this._lastActiveQuestionId = prevQuestionID

    const scrollToEl = () => {
      const el = this.getQuestionElement(prevQuestionID)

      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    // ✅ Wait for render if needed
    if (needsRerender) {
      // When loading all questions, need more time for DOM updates
      setTimeout(() => {
        requestAnimationFrame(() => {
          setTimeout(scrollToEl, 100)
        })
      }, 200)
    } else {
      scrollToEl()
    }
  }

  renderNextQuestion = (id, submission) => {
    if (!this._isMounted) return
    this.pauseAllMedia()
    this.suppressLayoutScroll()

    const {
      addQuestion,
      webSurvey,
      onSaveInstrumentToLocalStorage,
      isPauseResumeSupported,
    } = this.props
    const { order, questions } = webSurvey

    const nextQId = getNextRenderableQuestionId(webSurvey, id, (qid) =>
      this.nextQuestionId(qid),
    )
    const isNextQSiblingType =
      questions[nextQId] && !!questions[nextQId].sibling_id
    if (!isNextQSiblingType) addQuestion([nextQId], order[0], submission)

    setTimeout(() => {
      let nextSiblingQuestionIds = []
      if (isNextQSiblingType) {
        nextSiblingQuestionIds = this.nextSiblingQuestionIds(nextQId) || []
      }
      if (nextSiblingQuestionIds.length) {
        addQuestion(nextSiblingQuestionIds, order[0], submission)
      }
    }, 500)

    // Autosave to localStorage after advancing to next question
    // This ensures progress is preserved even if user closes browser
    setTimeout(() => {
      if (onSaveInstrumentToLocalStorage && isPauseResumeSupported) {
        onSaveInstrumentToLocalStorage()
      }
    }, 500) // Small delay to ensure Redux state is updated

    const tryScroll = (retries = 10) => {
      if (!this._isMounted) return
      const el = this.getQuestionElement(nextQId)

      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else if (retries > 0) {
        setTimeout(() => tryScroll(retries - 1), 100)
      }
    }

    requestAnimationFrame(() => {
      this._lastActiveQuestionId = nextQId
      this.updateProgress(nextQId)
      tryScroll()
    })
  }

  renderQuitModal = () => {
    const {
      openModal,
      humiIsabellaDisabled,
      webSurvey,
      strings,
      sendEvent,
      onSaveInstrumentToLocalStorage,
      isPauseResumeSupported,
    } = this.props

    if (isPauseResumeSupported) {
      openModal({
        heading: 'Quit survey?',
        content: 'We will save your progress.',
        confirmButton: 'Save and quit',
        cancelButton: 'Return',
        closeOnBackgroundClick: true,
        humi: !humiIsabellaDisabled,
        className: 'web-survey-close',
        onConfirm: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.instrumentUpdated,
            tags: {
              flow: 'instrument',
              action: 'updated',
              instrument_id: webSurvey?.id != null ? String(webSurvey.id) : undefined,
              instance_id:
                webSurvey?.instance_id != null
                  ? String(webSurvey.instance_id)
                  : undefined,
            },
          })
          // Save multiple times before navigating so in-flight state updates
          // from current question components are captured reliably.
          onSaveInstrumentToLocalStorage()
          const saveTimeout = setTimeout(() => {
            if (!this._isMounted) return
            onSaveInstrumentToLocalStorage()
          }, 250)
          this.timeoutIds.push(saveTimeout)
          const quitTimeout = setTimeout(() => {
            if (!this._isMounted) return
            onSaveInstrumentToLocalStorage()
            navigateTo('/my-activities')
          }, 700)
          this.timeoutIds.push(quitTimeout)
        },
      })
    } else {
      openModal({
        heading: strings.are_you_sure,
        content: strings.survey_quit_modal,
        confirmButton: strings.survey_quit_confirm,
        cancelButton: strings.back_survey,
        closeOnBackgroundClick: true,
        humi: !humiIsabellaDisabled,
        className: 'web-survey-close',
        onConfirm: () => {
          logSentryEvent({
            message: 'Instrument lifecycle: quit_without_save',
            tags: {
              flow: 'instrument-lifecycle',
              'instrument.action': 'quit_without_save'
            },
            contexts: {
              instrument: {
                id: webSurvey?.id
              }
            }
          })
          sendEvent(webSurvey?.id, INSTRUMENT_EVENTS_MAP.cancelled)
          navigateTo('/my-activities')
        },
      })
    }
  }

  onScroll = () => {
    if (!this._isMounted) return
    const { scrollTop } = this.state
    const windowPos = window.pageYOffset
    if (windowPos > scrollTop) {
      this.setState({ scrollingDown: true, scrollingUp: false })
    } else {
      this.setState({ scrollingDown: false, scrollingUp: true })
    }
    this.setState({ scrollTop: windowPos })
  }

  renderCameraHelpModal = () => {
    const { closeModal, openModal, strings, humiIsabellaDisabled } = this.props
    openModal({
      cancelButton: strings.cancel_btn,
      className: 'permission cam-permission-help',
      closeOnBackgroundClick: true,
      confirmButton: strings.got_it || 'Got it',
      content:
        strings.photo_submission_requires_camera_help ||
        'If the option to take a photo is not available or if the camera screen is blank, please go to your device settings and allow camera access for this browser.',
      heading:
        strings.photo_submission_requires_camera ||
        'This instrument requires camera access',
      humi: !humiIsabellaDisabled,
      onConfirm: closeModal,
      useWideButtons: true,
    })
  }

  renderDeleteConfirmationModal = (onDelete) => {
    const { openModal, strings, humiIsabellaDisabled } = this.props
    openModal({
      cancelButton: strings.cancel_btn,
      className: 'permission delete-permission',
      closeOnBackgroundClick: true,
      confirmButton: strings.delete_it,
      content:
        strings.requires_delete_permission_warning ||
        'This action cannot be undone.',
      heading:
        strings.requires_delete_permission ||
        'Are you sure you want to delete this photo?',
      humi: !humiIsabellaDisabled,
      onConfirm: onDelete,
      useWideButtons: true,
    })
  }

  getInstrumentParticipantScores = () => {
    const { webSurvey, submissionSurvey } = this.props
    return computeParticipantScores({ webSurvey, submissionSurvey })
  }

  getInstrumentScoreTotal = () => {
    const { webSurvey, submissionSurvey } = this.props
    return computeInstrumentScoreTotal({ webSurvey, submissionSurvey })
  }

  render() {
    const { webSurvey, isArticle, strings, currentSurvey } = this.props
    const { currentProgress, completionDivisor, browser } = this.state
    return (
      <div
        ref={this.surveyContainerRef}
        onScroll={this.onSurveyContainerScroll}
        className={`websurvey-container ${browser}`}>
        <div className='close-bar'>
          <Button onClick={() => this.renderQuitModal()}>
            <div className='close-x'>{closeIcon()}</div>
            <span>{strings.quit}</span>
          </Button>
        </div>
        {!isArticle && this.renderIntroduction(webSurvey)}
        {!isArticle && this.renderQuestions(webSurvey)}
        <PercentMeter
          hideNum
          invariableColor
          ratio={currentProgress / completionDivisor}
        />
      </div>
    )
  }
}

export default WebSurveyPage

WebSurveyPage.propTypes = {
  addQuestion: PropTypes.func,
  checkLogic: PropTypes.func,
  completeQuestion: PropTypes.func,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  fetchPendingInstruments: PropTypes.func,
  fetchInstruments: PropTypes.func,
  firstName: PropTypes.string,
  isArticle: PropTypes.bool,
  isCaregiver: PropTypes.bool,
  onSaveInstrumentToLocalStorage: PropTypes.func,
  onSurveySubmit: PropTypes.func,
  openModal: PropTypes.func,
  pendingInstruments: PropTypes.arrayOf(PropTypes.object),
  instruments: PropTypes.arrayOf(PropTypes.object),
  resetCurrentSurvey: PropTypes.func,
  selectedLocale: PropTypes.string,
  setAllRequiredAnswered: PropTypes.func,
  setSubmissionSurvey: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  submissionLoading: PropTypes.bool,
  submissionSurvey: PropTypes.shape({
    questions: PropTypes.array,
  }),
  webSurvey: PropTypes.shape({
    order: PropTypes.array,
    questions: PropTypes.object,
  }),
}
